// Teams compared on a Stats tile (Ben, 2026-11-01: "add a second team … in the same way I can add
// a 2/3/4/5 player", across leagues). The player comparison's look: a chip per team (its colour,
// its name and season, ✕ to take it off, drag it out as the team), a dot per team on each bar,
// both numbers when there are two. Each team is read from its own league season; the track runs
// from the lowest to the highest of every team in the leagues on the tile, so teams from two
// leagues share one scale. The For / Against button switches them all. How teams join:
// canvas/team-merge.ts.

import { useEffect, useReducer, type PointerEvent as ReactPointerEvent } from "react";
import type { TeamStatsResponse } from "@catbento/shared";
import { getModState, setModState } from "./bridge.js";
import { PopulationReadout } from "./PopulationReadout.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainTeamStatsMerged } from "./explain.js";
import { markerFraction } from "./stats-view.js";
import { formatMetricValue } from "../engines/stats-filter-kit.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { useLiveQueries } from "../live/query.js";
import { liveApi } from "../live/api.js";
import { leagueSeasonsQuery, loadTeamStats, teamComparisonTrack } from "../live/team-stats.js";
import { startTeamTokenDrag } from "../canvas/menu-drag.js";
import { unmergeTeam } from "../canvas/team-merge.js";
import { COMPARE_LETTERS } from "../canvas/drop-rules.js";
import type { TeamRef } from "../live/team.js";

type Side = "for" | "against";
const SIDE_LABELS: Record<string, string> = { for: "For", against: "Against" };

export function TeamStatsComparison({ mod, body, teams, categoryId }: { mod: HTMLElement; body: HTMLElement; teams: TeamRef[]; categoryId: string }) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const leagues = useLiveLeagueChoices();
  const side: Side = getModState<Side>(mod, "_teamStatsSide") === "against" ? "against" : "for";
  const colors = getModState<string[]>(mod, "_mergedTeamColors") ?? [...COMPARE_LETTERS];

  // No single team to pick across a comparison: the header's search box hides, as for players.
  useEffect(() => {
    body.style.justifyContent = "flex-start";
    const strip = mod.querySelector<HTMLElement>(":scope > .module-sub-controls");
    if (!strip) return;
    const prev = strip.style.display;
    strip.style.display = "none";
    return () => {
      strip.style.display = prev;
    };
  }, [mod, body]);

  // A team without a season shows its league's newest (as on its own tile).
  const unseasoned = [...new Set(teams.filter((t) => t.season === null).map((t) => t.competitionId))];
  const newest = useLiveQueries(unseasoned.map(leagueSeasonsQuery));
  const seasonOf = (t: TeamRef): string | null => t.season ?? newest[unseasoned.indexOf(t.competitionId)]?.[0] ?? null;
  const seasons = teams.map(seasonOf);
  // One answer per league season (two teams from one league share it), the same answer a lone tile reads.
  const answers = useLiveQueries(
    teams.map((t, i) => ({
      key: `team-stats:${t.competitionId}:${seasons[i]}:${categoryId}`,
      load: (): Promise<TeamStatsResponse | null> =>
        seasons[i] === null ? Promise.resolve(null) : loadTeamStats(liveApi(), t.competitionId, seasons[i] as string, categoryId),
    })),
  );

  const flip = () => {
    setModState(mod, "_teamStatsSide", side === "for" ? "against" : undefined);
    forceRender();
  };
  const first = answers.find((a): a is TeamStatsResponse => a !== null) ?? null;
  const groups = first ? first[side] : [];
  const hasAgainst = !!first && first.against.some((g) => g.metrics.length > 0);
  const showValues = teams.length === 2;
  const leagueName = (t: TeamRef) => leagues.find((c) => c.id === t.competitionId)?.label ?? "league";
  const valueOf = (i: number, key: string): number | null => {
    const v = answers[i]?.rows.find((r) => r.teamId === teams[i]!.id)?.values[key];
    return v === null || v === undefined || Number.isNaN(v) ? null : v;
  };

  const onChipDrag = (e: ReactPointerEvent<HTMLElement>, t: TeamRef, season: string | null) => {
    if (window.innerWidth <= 768 || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    startTeamTokenDrag(e.nativeEvent, { ...t, season });
  };

  return (
    <>
      <ModuleExplainer
        mod={mod}
        paragraphs={explainTeamStatsMerged({
          side,
          hasAgainst,
          teams: teams.map((t, i) => ({ name: t.name, league: leagueName(t), season: seasons[i] ? humanizeSeasonCode(seasons[i] as string) : "" })),
        })}
      />
      <div className="compare-wrap">
        <PopulationReadout mod={mod} body={body} count={teams.length} showToggle={hasAgainst} mode={side} onToggle={flip} label="teams compared" modeLabels={SIDE_LABELS} />
        <div className="compare-legend">
          {teams.map((t, i) => (
            <span
              key={i}
              className={`compare-chip compare-chip-${colors[i]}`}
              style={window.innerWidth > 768 ? { cursor: "grab" } : undefined}
              onPointerDown={(e) => {
                if ((e.target as HTMLElement).closest(".compare-remove")) return;
                onChipDrag(e, t, seasons[i] ?? null);
              }}
            >
              <span className="compare-dot" />
              {t.name} <span className="compare-chip-season">({seasons[i] ? humanizeSeasonCode(seasons[i] as string) : "—"})</span>
              <span className="compare-remove" onClick={() => unmergeTeam(mod, i)}>
                ✕
              </span>
            </span>
          ))}
        </div>
        <div className="stats-rows">
          {groups.map((g) => (
            <div className="stat-group" key={g.id}>
              {groups.length > 1 && <div className="stat-group-label">{g.name}</div>}
              {g.metrics.map((m) => {
                const track = teamComparisonTrack(answers, m.key);
                return (
                  <div className="pbar-row" data-key={m.key} key={m.key}>
                    <span className="pbar-label-wrap">
                      <span className="pbar-label">{m.shortName || m.name}</span>
                      <span className="pbar-info-icon" data-tooltip={m.definition}>
                        ⓘ
                      </span>
                    </span>
                    <span className="pbar-track" style={!track ? { visibility: "hidden" } : undefined}>
                      {teams.map((t, i) => {
                        const v = valueOf(i, m.key);
                        const missing = v === null || !track;
                        return (
                          <span
                            key={i}
                            className={`pbar-marker marker-${colors[i]}`}
                            style={missing ? { opacity: 0 } : { opacity: 1, left: `${(markerFraction(v, track) * 100).toFixed(1)}%` }}
                            {...(missing ? {} : { "data-tooltip": `${t.name}: ${formatMetricValue(v, m.outputType)}` })}
                          />
                        );
                      })}
                    </span>
                    {showValues && (
                      <span className="pbar-value pbar-value-compare">
                        {teams.map((_, i) => (
                          <span key={i} className={`compare-value-${colors[i]}`}>
                            {formatMetricValue(valueOf(i, m.key), m.outputType)}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
